import React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { ImportSessionResponse } from "../../../../../api/frameworksApi";
import { Modal } from "../../../../components/Modal";

export interface ImportSessionDialogProps {
  open: boolean;
  onClose: () => void;
  sspSessionData: ImportSessionResponse | null;
  setSspSessionData: React.Dispatch<
    React.SetStateAction<ImportSessionResponse | null>
  >;
  sspSessionLoading: boolean;
  sspSessionError: string | null;
  sspCommitting: boolean;
  sspAborting: boolean;
  handleSspCommitSession: () => void;
  handleSspAbortSession: () => void;
  resetSspImport: () => void;
  onCommittedDone: () => void;
}

export function ImportSessionDialog({
  open,
  onClose,
  sspSessionData,
  setSspSessionData,
  sspSessionLoading,
  sspSessionError,
  sspCommitting,
  sspAborting,
  handleSspCommitSession,
  handleSspAbortSession,
  resetSspImport,
  onCommittedDone,
}: ImportSessionDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        sspSessionData
          ? sspSessionData.jobStatus.toLowerCase() === "committed"
            ? "Import Complete"
            : sspSessionData.jobStatus.toLowerCase() === "validated"
              ? "Commit SSP"
              : sspSessionData.jobStatus.toLowerCase() === "failed" ||
                sspSessionData.jobStatus.toLowerCase() === "aborted"
                ? "Import Failed"
                : "Importing SSP"
          : "Import SSP"
      }
      size="md"
      closable={false}
      footer={
        sspSessionData ? (
          <div className="flex justify-end w-full gap-2">
            {sspSessionData.jobStatus.toLowerCase() === "committed" ? (
              <button
                onClick={() => {
                  onCommittedDone();
                  setSspSessionData(null);
                  resetSspImport();
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md"
              >
                Done
              </button>
            ) : sspSessionData.jobStatus.toLowerCase() === "failed" ||
              sspSessionData.jobStatus.toLowerCase() === "aborted" ? (
              <button
                onClick={() => {
                  onClose();
                  setSspSessionData(null);
                  resetSspImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  onClick={handleSspAbortSession}
                  disabled={sspAborting || sspCommitting}
                  className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                >
                  {sspAborting ? "Cancelling…" : "Cancel"}
                </button>
                <button
                  onClick={handleSspCommitSession}
                  disabled={
                    sspCommitting ||
                    sspAborting ||
                    sspSessionData.jobStatus.toLowerCase() !== "validated"
                  }
                  className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${sspCommitting ||
                    sspAborting ||
                    sspSessionData.jobStatus.toLowerCase() !== "validated"
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  {sspCommitting ? "Committing…" : "Commit"}
                </button>
              </>
            )}
          </div>
        ) : null
      }
    >
      {sspSessionLoading && (
        <div className="py-12 flex flex-col items-center gap-4">
          <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
          <p className="text-sm font-medium">Loading session…</p>
        </div>
      )}
      {sspSessionError && (
        <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
          <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
          <p className="text-xs">{sspSessionError}</p>
        </div>
      )}
      {sspSessionData && (
        <div className="space-y-5">
          {sspCommitting && (
            <div className="py-6 flex flex-col items-center gap-3">
              <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
              <p className="text-sm font-medium">Committing import…</p>
              <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                  style={{ width: "70%" }}
                />
              </div>
            </div>
          )}
          {!sspCommitting && (
            <>
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-xs text-muted-foreground">
                    Status
                  </span>
                  <span
                    className={`text-sm font-semibold capitalize ${sspSessionData.jobStatus.toLowerCase() === "committed"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : sspSessionData.jobStatus.toLowerCase() ===
                        "failed" ||
                        sspSessionData.jobStatus.toLowerCase() ===
                        "aborted"
                        ? "text-red-600 dark:text-red-400"
                        : sspSessionData.jobStatus.toLowerCase() ===
                          "validated"
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-amber-600 dark:text-amber-400"
                      }`}
                  >
                    {sspSessionData.jobStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-xs text-muted-foreground">
                    Job ID
                  </span>
                  <code className="text-xs font-mono text-foreground">
                    {sspSessionData.id}
                  </code>
                </div>
              </div>
              {sspSessionData.findings.length > 0 && (
                <div className="rounded-lg border border-border p-4">
                  <h4 className="text-xs font-bold text-muted-foreground mb-2">
                    Findings ({sspSessionData.findings.length})
                  </h4>
                  <div className="space-y-2">
                    {sspSessionData.findings.map((f) => (
                      <div
                        key={f.id}
                        className="flex items-start gap-2 text-xs"
                      >
                        <span
                          className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "info"
                            ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                            : f.severity === "warning"
                              ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                              : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                            }`}
                        >
                          {f.severity}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium">{f.title}</p>
                          <p className="text-muted-foreground">
                            {f.detail}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
